import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../../../models';
import { EnvironmentKey, IEnvironmentComponentProps } from '../../../../../models/AppState';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { TagListAutoCompleteViewModel } from '../../../../../viewmodels/AutoCompleteViewModels';
import { baseStyleSheet } from '../../../../styles/styles';
import { Checkbox } from '../../../Checkbox';
import { Modal } from '../../../Modal';
import {
	ISimpleAutoCompleteSearchFieldEvent,
	ISimpleAutoCompleteSearchFieldItemSelectionEvent,
	SimpleAutoCompleteSearchField,
} from '../../../autocomplete/SimpleAutoCompleteSearchField';
import { SearchIcon } from '../../../svgs/icons/SearchIcon';
import { Tag } from '../../../tags/Tag';
import { styleSheet } from './styles';

interface IProps extends IEnvironmentComponentProps, IImpersonationContextComponentProps {
	isOpen: boolean;
	onRequestClose(): void;
	tags?: string[];
	onConfirm(containsAll: boolean, tagValues: string[], exclude: boolean): void;
	initialTags?: string[];
	initialContainsAll?: boolean;
	initialExclude?: boolean;
}

export const SimpleTagSelector: React.FC<IProps> = inject(
	EnvironmentKey,
	ImpersonationContextKey
)(
	observer(props => {
		const { isOpen, onRequestClose, tags, onConfirm, initialTags, initialContainsAll, initialExclude } = props;
		const [tagsToInclude, setTagsToInclude] = React.useState(initialTags || []);
		const [containsAllTags, setSendOption] = React.useState<boolean>(initialContainsAll || false);
		const [excludeTags, setExcludeByTags] = React.useState<boolean>(initialExclude || false);
		const userSession = useUserSession();

		const removeTag = React.useCallback(
			(tag: string) => () => setTagsToInclude(prev => prev.filter(x => x !== tag)),
			[]
		);
		const onCheckClick = (e: React.ChangeEvent<HTMLInputElement>) => setSendOption(e?.target?.checked);
		const onCheckClickExclude = (e: React.ChangeEvent<HTMLInputElement>) => setExcludeByTags(e?.target?.checked);

		// #region autocomplete
		const autocompleteVm = React.useRef(
			new TagListAutoCompleteViewModel(userSession, tags).impersonate(
				props.impersonationContext
			) as Api.ResourceAutoCompleteViewModel
		).current;

		const onCreateAutocompleteViewModel = React.useCallback(() => {
			return autocompleteVm;
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const onTagSelected = React.useCallback(
			(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent) => {
				if (typeof e.selection === 'string') {
					setTagsToInclude(Array.from(new Set([...tagsToInclude, e.selection])));
				}
				e.target?.clearInput();
			},
			[tagsToInclude]
		);

		const onBlur = React.useCallback((e: ISimpleAutoCompleteSearchFieldEvent<React.FocusEvent<HTMLInputElement>>) => {
			e.target?.clearInput();
		}, []);
		// #endregion

		const onConfirmClicked = React.useCallback(() => {
			return onConfirm?.(containsAllTags, tagsToInclude, excludeTags);
		}, [onConfirm, containsAllTags, tagsToInclude, excludeTags]);

		const renderSelectedTags = React.useCallback(() => {
			return (
				<div className={css(styleSheet.selectedTags)}>
					<span className={css(styleSheet.inputTitle)}>{'Selected Tags: '}</span>
					{tagsToInclude.map(x => {
						return (
							<span className={css(styleSheet.time)} key={x}>
								<Tag tagValue={x} onRemoveButtonClicked={removeTag(x)} showRemoveButton={true} />
							</span>
						);
					})}
				</div>
			);
		}, [tagsToInclude, removeTag]);

		return (
			<Modal isOpen={isOpen} onRequestClose={onRequestClose} useDefaultHeader={true}>
				<div className={css(styleSheet.modalTitle)}>Choose Tags</div>
				<div className={css(styleSheet.inputTitle)}>Select tags from your integration to segment by</div>
				{renderSelectedTags()}
				<Checkbox
					id='new-content-checkbox'
					type='small'
					onChange={onCheckClick}
					checked={containsAllTags}
					className={css(styleSheet.newContentCheckbox)}
				>
					<div className={css(styleSheet.textBlack)}>Must contain all selected tags</div>
				</Checkbox>
				<Checkbox
					id='exclude-by-checkbox'
					type='small'
					onChange={onCheckClickExclude}
					checked={excludeTags}
					className={css(styleSheet.newContentCheckbox)}
				>
					<div className={css(styleSheet.textBlack)}>Exclude by selected tags.</div>
				</Checkbox>

				<SimpleAutoCompleteSearchField
					leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} />}
					onBlur={onBlur}
					onCreateAutoCompleteViewModel={onCreateAutocompleteViewModel}
					onItemSelected={onTagSelected}
					pageSize={10}
					resultsLimit={10}
					type={Api.ResourceAutoCompleteViewModelType.Tag}
				/>
				<div className={css(styleSheet.modalButtons)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onConfirmClicked}>
						<span>Save</span>
					</button>
				</div>
			</Modal>
		);
	})
);
